import {
	type DropdownMenuPortalProps,
	Group,
	Portal,
	Root,
	Trigger
} from '@radix-ui/react-dropdown-menu'
import { forwardRef, useMemo } from 'react'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import {
	DropdownContent,
	DropdownItem,
	DropdownLabel,
	DropdownSeparator
} from '@/components/ui/dropdown-atoms'
import { ArrowRightIcon } from '@/components/ui/icons'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { usePhotosetStore } from '@/features/photoset/store/store'
import type { OnAllocateAction, PhotosetSpace } from '@/features/photoset/types'
import { spaceNameToLabel } from '@/features/photoset/utils'
import { objectValues } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'

export const AllocateDropdown = forwardRef<
	HTMLDivElement,
	{
		container?: DropdownMenuPortalProps['container']
		excludedSpaces?: PhotosetSpace['space']['key_name'][]
		onAllocate: (values: OnAllocateAction) => void
	}
>(({ container, onAllocate, excludedSpaces }, ref) => {
	const { t } = useTranslation()
	const photosetSpacesDict = usePhotosetStore(
		(state) => state.photosetSpacesDict
	)
	const selectedImages = usePhotosetStore((store) => store.selectedImages)
	const photosetSpaces = useMemo(
		() => objectValues(photosetSpacesDict),
		[photosetSpacesDict]
	)
	const otherSpace = photosetSpaces.find(
		(space) => space.space.key_name === 'other'
	)

	const onSelectSpace = (photosetSpaceId: PhotosetSpace['id']) => {
		onAllocate({
			photosetSpaceId,
			imagesIds: selectedImages,
			trackInHistory: true,
			isShortcut: false,
			keepSelected: false
		})
	}

	return (
		<Root>
			<Trigger asChild disabled={!objectValues(photosetSpaces).length}>
				<ButtonLegacy size='sm' type='button' variant='secondary'>
					<ArrowRightIcon />
					{t('common:move_to')}
				</ButtonLegacy>
			</Trigger>
			<Portal container={container}>
				<DropdownContent
					align='end'
					className='grid auto-rows-[auto_minmax(0,1fr)] overflow-hidden'
					ref={ref}
					side='bottom'
					sideOffset={8}
				>
					<DropdownLabel>{t('common:move_to')}</DropdownLabel>
					<ScrollArea scrollbar={<Scrollbar />}>
						<Group>
							{photosetSpaces.map((photosetSpace) => {
								if (
									photosetSpace.space.key_name === 'other' ||
									excludedSpaces?.includes(
										photosetSpace.space.key_name
									)
								) {
									return false
								}

								return (
									<DropdownItem
										key={photosetSpace.space.key_name}
										onClick={() =>
											onSelectSpace(photosetSpace.id)
										}
									>
										{
											spaceNameToLabel[
												photosetSpace.space.key_name
											]
										}
									</DropdownItem>
								)
							})}
						</Group>
						{!!otherSpace && (
							<>
								<DropdownSeparator />
								<Group>
									<DropdownItem
										onClick={() =>
											onSelectSpace(otherSpace.id)
										}
									>
										{spaceNameToLabel.other}
									</DropdownItem>
								</Group>
							</>
						)}
					</ScrollArea>
				</DropdownContent>
			</Portal>
		</Root>
	)
})

AllocateDropdown.displayName = 'AllocateDropdown'
